// Sound (design 8 item 6, owner P6): a small set, off by default, the volume remembered with the account. What each sound is lives
// in game/synth. Which sounds a view update calls for is a pure function (soundsFor), tested; playing them is the browser's business.

import { PromptKind } from "../pb/mj/protocol/v1/protocol_pb";
import { RATE, TAKES, samples } from "./synth";
import type { Sound } from "./synth";
import type { View } from "./view";

export type { Sound };

/** The sounds that going from `prev` to `next` calls for, in the order they play. A discard's click is not among them: it plays as the tile lands, from the table (design 10 §8). */
export function soundsFor(prev: View, next: View, noAssists = false): Sound[] {
  if (next.gameId !== prev.gameId || next.hand?.index !== prev.hand?.index) return [];   // a new game or hand: silence
  const out: Sound[] = [];
  const c = next.callout;
  if (c && c.seq !== prev.callout?.seq) out.push(calloutSound(c.action, c.seat === next.seat));
  const p = next.prompt;
  // Without assists nothing tells a seat it could call (design 14 §6.6).
  const hidden = noAssists && (p?.prompt.kind === PromptKind.WINDOW || p?.prompt.kind === PromptKind.LAST_TILE_OFFER);
  if (p && !p.sent && !hidden && p.prompt.promptId !== prev.prompt?.prompt.promptId) out.push("turn");
  const drawn = (v: View) => !!(v.result?.exhaustive || v.result?.abortive);
  if (drawn(next) && !drawn(prev)) out.push("draw");
  if (next.gameEnd && !prev.gameEnd) out.push("over");
  return out;
}

function calloutSound(action: string, own: boolean): Sound {
  switch (action) {
    case "DECLARE_RIICHI":
      return "riichi";
    case "RON":
    case "TSUMO":
      return own ? "win" : "winOther";
    case "OPEN_KAN":
    case "CLOSED_KAN":
    case "ADDED_KAN":
      return "kan";
    case "WARNING":
    case "DEAD_HAND":
    case "FINE":
    case "CHONBO":
      return "warning";
    default:
      return "call";
  }
}

// A browser holds a page's audio until the page is touched, and its clock with it. A sound scheduled then would wait, and every one of
// them would play at once at the first tap (seen 2026-11-01: a tab restored on a table). So a sound plays now or not at all.
const STALE_MS = 200;

let context: AudioContext | null = null;
const buffers = new Map<string, AudioBuffer>();
let take = 0;
let lastClick = -Infinity;

function audio(): AudioContext | null {
  if (typeof AudioContext === "undefined") return null;
  if (!context) {
    context = new AudioContext();
    // Mixed with whatever else the phone plays, not in place of it, and silent under the mute switch (Safari 16.4 on).
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (session) session.type = "ambient";
    const wake = () => {
      if (context?.state !== "running") void context?.resume();
    };
    // Those a browser counts as being touched; a phone call or another app leaves it "interrupted", to be woken the same way.
    for (const e of ["pointerup", "touchend", "click", "keydown"]) addEventListener(e, wake, { capture: true, passive: true });
    document.addEventListener("visibilitychange", wake);
  }
  return context;
}

function bufferOf(ctx: AudioContext, sound: Sound, n: number): AudioBuffer {
  const key = `${sound}${n}`;
  let b = buffers.get(key);
  if (!b) {
    const s = samples(sound, n);
    b = ctx.createBuffer(1, s.length, RATE);
    b.copyToChannel(s as Float32Array<ArrayBuffer>, 0);
    buffers.set(key, b);
  }
  return b;
}

/** Plays `sound` at `volume` (0-100; heard roughly in proportion, so its square is what scales). */
export function play(sound: Sound, volume: number): void {
  const ctx = audio();
  if (!ctx || volume <= 0) return;
  const click = sound === "discard" || sound === "discardFar";
  if (click) {
    // Tiles landing together, as when a tab comes back to its table, are one click.
    const now = performance.now();
    if (now - lastClick < 40) return;
    lastClick = now;
  }
  const source = ctx.createBufferSource();
  source.buffer = bufferOf(ctx, sound, click ? take++ % TAKES : 0);
  const out = ctx.createGain();
  out.gain.value = (volume / 100) ** 2;
  source.connect(out).connect(ctx.destination);
  if (ctx.state === "running") return source.start();
  const asked = performance.now();
  void ctx.resume().then(() => {
    if (performance.now() - asked < STALE_MS) source.start();
  });
}
